/**
 * Structured Message Content Types
 *
 * This type system treats message content as an array of blocks rather than a string.
 * Each block is either text or a mention object with metadata.
 *
 * Benefits:
 * - No regex parsing needed
 * - Clear boundaries between mentions and text
 * - Easy to render as chips/components
 * - Similar to Slack, Notion content models
 */

export interface TextBlock {
  type: 'text';
  content: string;
}

export interface MentionBlock {
  type: 'mention';
  userId: string;        // Firebase UID or employee_id
  email: string;         // User's email for notifications
  displayName: string;   // Display name (e.g., "Ross McGowan")
}

export type ContentBlock = TextBlock | MentionBlock;


/**
 * Helper to convert structured content to storage format
 * Uses @[Name](email) format for backend compatibility
 */
export function serializeContent(blocks: ContentBlock[]): string {
  return blocks
    .map(block => {
      if (block.type === 'mention') {
        // Use @[Name](email) format for backend compatibility
        return `@[${block.displayName}](${block.email})`;
      }
      return block.content;
    })
    .join('');
}

/**
 * Helper to convert stored text back to structured content
 * Handles @[Name](email) format
 */
export function deserializeContent(text: string): ContentBlock[] {
  const blocks: ContentBlock[] = [];

  // Pattern for @[Name](email) format
  const mentionPattern = /@\[([^\]]+)\]\(([^)]+)\)/g;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = mentionPattern.exec(text)) !== null) {
    // Add text before this mention
    if (match.index > lastIndex) {
      const textContent = text.slice(lastIndex, match.index);
      if (textContent) {
        blocks.push({ type: 'text', content: textContent });
      }
    }

    // Add the mention block
    blocks.push({
      type: 'mention',
      userId: match[2], // Using email as userId
      email: match[2],
      displayName: match[1]
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    if (remainingText) {
      blocks.push({ type: 'text', content: remainingText });
    }
  }

  // If no mentions found, treat entire text as one text block
  if (blocks.length === 0 && text) {
    blocks.push({ type: 'text', content: text });
  }

  return blocks;
}

/**
 * Extract all mention emails from structured content
 */
export function extractMentionEmails(blocks: ContentBlock[]): string[] {
  return blocks
    .filter((block): block is MentionBlock => block.type === 'mention')
    .map(block => block.email);
}

/**
 * Convert structured content to plain text (for display/search)
 */
export function toPlainText(blocks: ContentBlock[]): string {
  return blocks
    .map(block => {
      if (block.type === 'mention') {
        return `@${block.displayName}`;
      }
      return block.content;
    })
    .join('');
}
